import {
  CalendarTimeRangeConfig,
  FixedTimeRangeConfig,
  RelativeTimeRangeConfig,
} from '@highimpact-frontend/api-types';
import { getStartAndEndDate } from '@highimpact/time-intervals';
import { Layout, LayoutAxis, PlotData } from 'plotly.js-dist-min';
import { max, min, sort } from '../../../../internal/utils/array';
import { deNull } from '../../../../internal/utils/functions';
import { numberOrder, stringOrder } from '../../../../internal/utils/Order';
import { mapWithKeys, reduceWithKeys } from '../../../../internal/utils/record';

/**
 *
 * @description Returns a default layout for a plotly graph
 */
export function getDefaultLayout(layout: Partial<Layout>): Partial<Layout> {
  return {
    title: { text: '' },
    font: {
      family: '"Poppins Regular", sans-serif',
    },
    autosize: true,
    showlegend: true,
    barmode: 'overlay',
    bargap: 0.05,
    hovermode: 'x unified',

    legend: {
      xanchor: 'left',
      yanchor: 'bottom',
      x: 0,
      y: 1,
      orientation: 'h',
    },

    margin: {
      t: 0,
      l: 64,
      r: 64,
      pad: 8,
    },

    ...layout,
  };
}

// -- axis name operations

/**
 *
 * @description determines if the provided axis name is an x-axis
 * - shorthand and full names are supported
 */
export function isXAxis(axis: string): boolean {
  return axis.startsWith('x');
}

/**
 *
 * @description determines if the provided axis name is a y-axis
 * - shorthand and full names are supported
 */
export function isYAxis(axis: string): boolean {
  return axis.startsWith('y');
}

/**
 *
 * @description Convert shorthand axis names to full axis names
 */
export function expandAxisName(axis: string): string {
  return axis.replace(/^(x|y)(\d+)$/, (_, prefix, id) => `${prefix}axis${id}`);
}

/**
 *
 * @description Convert full axis names to shorthand axis names
 */
export function contractAxisName(axis: string): string {
  return axis.replace(/^(x|y)axis(\d+)$/, (_, prefix, id) => `${prefix}${id}`);
}

/**
 *
 * @description Remove the integer part of the axis name if this integer is 1
 * - shorthand and full names are supported
 */
export function normalizeAxisName(axis: string): string {
  return axis.replace(
    /^(x|y)(axis)?(\d+)?$/,
    (_, prefix, axis, id) => `${prefix}${axis || ''}${id === '1' ? '' : id}`
  );
}

/**
 *
 * @description Get the integer part of the axis name, using "1" where no integer is found.
 * Works with both shorthand and full axis names
 */
export function getAxisNumber(axis: string): number {
  return parseInt(
    axis.replace(/^(x|y)(axis)?(\d+)?$/, (_, _prefix, _axis, id) => id || '1')
  );
}

/**
 *
 * @description Finds the first unused integer given a list of axis names
 * @example
 * const axes = ['xaxis', 'xaxis2', 'xaxis4'];
 * console.log(findUnusedAxisNumber(axes)); // 3
 */
export function findUnusedAxisNumber(axes: string[]): number {
  const used = new Set(axes.map(getAxisNumber));
  if (used.size === 0) {
    return 1;
  }
  for (let i = 1; i <= used.size; i++) {
    if (!used.has(i)) {
      return i;
    }
  }
  return used.size + 1;
}

interface AxesPrediction {
  x: string[];
  y: string[];
  xDefault?: string;
  yDefault?: string;
}

/**
 *
 * @description Predicts the set of axes that Plotly will use for a given dataset
 * - According to our observations, for each dataset,
 *  - if the yaxis is defined, its name is added to the set
 *  - if at least one yaxis is not defined, an additional yaxis is added to the set, which is attributed the smallest available id
 *  - the sequence of name is: yaxis, yaxis2, yaxis3, ...
 *  - the yaxis property of each dataset has values which are the shorthand form of the name: y, y2, y3, ...
 *  - all of the above is true for xaxis as well
 */
export function predictAxes(dataset: Partial<PlotData>[]): AxesPrediction {
  const xAxes = new Set<string>();
  const yAxes = new Set<string>();

  // Collect all axes explicitly defined in the dataset
  dataset.forEach((data) => {
    if (data.yaxis) {
      yAxes.add(expandAxisName(data.yaxis));
    }
    if (data.xaxis) {
      xAxes.add(expandAxisName(data.xaxis));
    }
  });

  // If at least one dataset does not define an axis define a default axis
  const xDefault =
    xAxes.size < dataset.length
      ? normalizeAxisName(`xaxis${findUnusedAxisNumber([...xAxes])}`)
      : undefined;
  const yDefault =
    yAxes.size < dataset.length
      ? normalizeAxisName(`yaxis${findUnusedAxisNumber([...yAxes])}`)
      : undefined;

  return {
    x: sort([...xAxes], stringOrder),
    y: sort([...yAxes], stringOrder),
    xDefault,
    yDefault,
  };
}

type AxisDatasetGrouping = Record<string, Partial<PlotData>[]>;

interface AxisDatasetGroup {
  x: AxisDatasetGrouping;
  y: AxisDatasetGrouping;
}

/**
 *
 * @description associates each predicted axis with the corresponding datasets
 */
export function groupByAxes(
  dataset: Partial<PlotData>[],
  axes: AxesPrediction
): AxisDatasetGroup {
  const xMap: AxisDatasetGrouping = {};
  const yMap: AxisDatasetGrouping = {};

  dataset.forEach((data) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const x = data.xaxis ? expandAxisName(data.xaxis) : axes.xDefault!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const y = data.yaxis ? expandAxisName(data.yaxis) : axes.yDefault!;

    if (!xMap[x]) {
      xMap[x] = [];
    }
    xMap[x].push(data);

    if (!yMap[y]) {
      yMap[y] = [];
    }
    yMap[y].push(data);
  });

  return {
    x: xMap,
    y: yMap,
  };
}

// -- axes operations

/**
 *
 * @description Check if the provided axis is a time axis
 */
export function isTimeAxis(axis: Partial<LayoutAxis>): boolean {
  return axis.type === 'date';
}

interface ComputedRanges {
  [key: string]: [number, number];
}

/**
 *
 * @description Check if a string matches the ISO 8601 format `yyyy-mm-ddThh:mm:ss` and appends a Z if it does
 */
function ensureIso(dateStr: string): string {
  const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

  if (isoPattern.test(dateStr)) {
    return dateStr + 'Z';
  }

  return dateStr;
}

/**
 *
 * @description Compute the range of each axis based on the provided datasets and the original layout
 */
export function computeRanges(
  layout: Partial<Layout>,
  dataset: Partial<PlotData>[],
  timeIntervals?:
    | CalendarTimeRangeConfig
    | FixedTimeRangeConfig
    | RelativeTimeRangeConfig
    | null
): ComputedRanges {
  const predictedAxes = predictAxes(dataset);
  const grouped = groupByAxes(dataset, predictedAxes);

  // associate each axis with the min of mins and max of maxes
  const xRanges = mapWithKeys((axis, data) => {
    if (timeIntervals) {
      return getStartAndEndDate(timeIntervals)
        .map(ensureIso)
        .map((d) => new Date(d).getTime()) as [number, number];
    }
    const ranges = data.map((d) => {
      // @ts-expect-error Plotly Layout is not well defined
      if (layout[axis] && isTimeAxis(layout[axis])) {
        // @ts-expect-error Plotly Layout is not well defined
        return [layout[axis].range[0], layout[axis].range[1]] as [
          string,
          string
        ];
      }
      // if the x axis is not a time axis but sill uses strings, it's unclear if there is any order
      if (d.x !== undefined && d.x[0] && typeof d.x[0] === 'string') {
        return [d.x.at(0), d.x.at(-1)];
      }

      const minX = min((d.x as number[]) || [], numberOrder);
      const maxX = max((d.x as number[]) || [], numberOrder);
      const defaultMin = deNull(
        // @ts-expect-error Plotly Layout is not well defined
        layout[axis]?.range ? layout[axis]?.range[0] : undefined,
        0
      );
      const defaultMax = deNull(
        // @ts-expect-error Plotly Layout is not well defined
        layout[axis]?.range ? layout[axis]?.range[1] : undefined,
        0
      );
      const correctedMinX = minX === undefined ? defaultMin : Math.min(0, minX);
      const correctedMaxX = maxX === undefined ? defaultMax : maxX;
      return [correctedMinX, correctedMaxX] as [number, number];
    });

    const minRange = min(
      ranges.map(([min]) => min),
      numberOrder
    );
    const maxRange = max(
      ranges.map(([, max]) => max),
      numberOrder
    );

    return [minRange, maxRange] as [number, number];
  }, grouped.x);

  const yRanges = mapWithKeys((axis, data) => {
    const ranges = data
      .filter((d) => d.y && d.y.length > 0)
      .map((d) => {
        const minY = min((d.y as number[]) || [], numberOrder);
        const maxY = max((d.y as number[]) || [], numberOrder);
        const defaultMin = deNull(
          // @ts-expect-error Plotly Layout is not well defined
          layout[axis]?.range ? layout[axis]?.range[0] : undefined,
          0
        );
        const defaultMax = deNull(
          // @ts-expect-error Plotly Layout is not well defined
          layout[axis]?.range ? layout[axis]?.range[1] : undefined,
          0
        );
        const correctedMinY =
          minY === undefined ? defaultMin : Math.min(0, minY);
        const correctedMaxY = maxY === undefined ? defaultMax : maxY;
        return [correctedMinY, correctedMaxY] as [number, number];
      });

    const minRange = min(
      ranges.map(([min]) => min),
      numberOrder
    );
    const maxRange = max(
      ranges.map(([, max]) => max),
      numberOrder
    );

    const correctedMinRange =
      minRange !== undefined ? minRange * 0.95 : minRange;
    const correctedMaxRange =
      maxRange !== undefined ? maxRange * 1.05 : maxRange;

    return [correctedMinRange, correctedMaxRange] as [number, number];
  }, grouped.y);

  return {
    ...xRanges,
    ...yRanges,
  };
}

/**
 *
 * @description round up to next multiple of ten
 */
const roundUpTens = (n: number) => Math.ceil(n / 10) * 10;

interface OmbrothermicScale {
  temperatures: [number, number];
  precipitations: [number, number];
}

/**
 * 
 * @description standardises the range of the two y-axes of the ombrothermic plot.
 *
Standardization rules:

#### If all temperatures are non-negative:
- The maximum temperature is set to half the maximum precipitation.

#### If any temperature is negative:
- The minimum precipitation is set to twice the minimum temperature.
- The maximum temperature is set to half the maximum precipitation.
 *
This standardization ensures that the ombrothermic plot visually reflects dry and wet periods:
 * - A day is considered **dry** when the temperature curve lies **above** the precipitation curve.
 */
export function standardiseOmbrothermicScale({
  temperatures: [minTemp, maxTemp],
  precipitations: [_minPrec, maxPrec],
}: OmbrothermicScale): OmbrothermicScale {
  const refAxisMax: keyof OmbrothermicScale =
    roundUpTens(maxPrec) > roundUpTens(maxTemp)
      ? 'precipitations'
      : 'temperatures';
  const newMaxTemp = refAxisMax === 'precipitations' ? maxPrec / 2 : maxTemp;
  const newMaxPrec = refAxisMax === 'temperatures' ? maxTemp * 2 : maxPrec;
  if (minTemp >= 0) {
    return {
      temperatures: [0, newMaxTemp],
      precipitations: [0, newMaxPrec],
    };
  }
  const newMinPrec = minTemp * 2;
  return {
    temperatures: [minTemp, newMaxTemp],
    precipitations: [newMinPrec, newMaxPrec],
  };
}

/**
 * 
 * @description given the default layout and the data of the ombrothermic plot,
 * returns the standardised y-axes ranges for this plot.
 * 
Standardization rules:

#### If all temperatures are non-negative:
- The maximum temperature is set to half the maximum precipitation.

#### If any temperature is negative:
- The minimum precipitation is set to twice the minimum temperature.
- The maximum temperature is set to half the maximum precipitation.
 *
This standardization ensures that the ombrothermic plot visually reflects dry and wet periods:
 * - A day is considered **dry** when the temperature curve lies **above** the precipitation curve.

 */
export function computeOmbrothermicRanges(
  layout: Partial<Layout>,
  dataset: Partial<PlotData>[]
): ComputedRanges {
  const computed = computeRanges(layout, dataset);
  const yAxesKeys = Object.keys(computed)
    .filter((k) => k.startsWith('y'))
    .sort();
  const precKey = yAxesKeys[0];
  const tempKey = yAxesKeys[1];
  const { precipitations, temperatures } = standardiseOmbrothermicScale({
    precipitations: computed[precKey],
    temperatures: computed[tempKey],
  });
  return {
    ...computed,
    [precKey]: precipitations,
    [tempKey]: temperatures,
  };
}

// -- layout operations

/**
 *
 * @description Apply the computed ranges to the provided layout
 */
export function applyRanges(
  layout: Partial<Layout>,
  ranges: ComputedRanges,
  preserveTime = true
): Partial<Layout> {
  const newLayout = reduceWithKeys(
    (acc, axis, [min, max]) => {
      // @ts-expect-error  Plotly Layout is not well defined
      if (acc[axis] && isTimeAxis(acc[axis]) && preserveTime) {
        return acc;
      }
      return {
        ...acc,
        [axis]: {
          // @ts-expect-error Plotly Layout is not well defined
          ...acc[axis],
          range: [min, max],
        },
      };
    },
    layout,
    ranges
  );

  return newLayout;
}

/**
 *
 * @description Modify the provided layout, ajusting the range of the axes
 * - time ranges are left untouched
 * - for each axis, the min and max of each dataset using it are computed, then min of the mins and max of the maxs are used
 */
export function adjustRanges(
  layout: Partial<Layout>,
  dataset: Partial<PlotData>[]
): Partial<Layout> {
  const allRanges = computeRanges(layout, dataset);

  return applyRanges(layout, allRanges);
}

export function findAxesKeys(layout: Partial<Layout>): string[] {
  return Object.keys(layout).filter((key) => key.match(/^(x|y)axis(\d*)$/));
}

export function getFirstXAxis(layout: Partial<Layout>): string | undefined {
  return findAxesKeys(layout).find(isXAxis);
}

export function addHistogramSpecificLayoutSettings(
  layout: Partial<Plotly.Layout>
): Partial<Plotly.Layout> {
  return {
    ...layout,
    bargap: 0.1,
    bargroupgap: 0.1,
    barmode: 'group' as const,
    hovermode: 'x unified' as const,
  };
}
